/**
 * 实时变更事件契约（后端 → 前端）。
 *
 * 前端只消费「什么变了」，不消费业务数据本身 —— 收到事件后由页面按既有请求重新拉取，
 * 因此不存在前后端数据一致性问题。
 *
 * ── 契约 v1（最小可用）────────────────────────────────────────────
 *   event: change
 *   data: {"scope":"all","ts":1730000000000}
 *
 * 可选细化：
 *   {"scope":"list","key":"/procurement/purchase-order","ts":...}
 *   {"scope":"detail","billType":"PURCHASE_ORDER","id":"123","ts":...}
 *   {"scope":"detail","key":"/procurement/purchase-order/detail","ts":...}
 *
 * 字段：
 *   scope  all    任意页面都视为脏（v1 广播）
 *          module 模块内的列表与详情；key 为模块路由前缀
 *          list   列表页；key 为列表路由（缺省时前端不匹配任何列表）
 *          detail 详情页；key 为详情路由，或 billType + id
 *   key    路由路径，可带 query（前端比较时忽略 query/hash 与末尾斜杠）
 *   billType 单据类型（与后端实体一致的大写枚举）
 *   id     单据主键，字符串或数字
 *   ts     后端发布时间戳（毫秒），前端不依赖它做顺序判断
 *
 * 未知字段忽略；无法识别的 scope 直接丢弃，不抛错、不影响页面。
 */
export type RealtimeScope = 'all' | 'module' | 'list' | 'detail';

export interface RealtimeChangeEvent {
  scope: RealtimeScope;
  key?: string;
  billType?: string;
  id?: string | number;
  ts?: number;
}

/** 页面侧匹配目标（由 useRealtimeRefresh / 既有刷新 hook 提供） */
export interface RealtimeMatchTarget {
  scope: RealtimeScope;
  key?: string;
  billType?: string;
  id?: string | number;
}

/** 去掉 query/hash 与末尾斜杠，保证事件 key 与页面 key 可比 */
export function normalizeRealtimeKey(key: string): string {
  const withoutQuery = key.trim().split(/[?#]/)[0];
  const normalized = withoutQuery.replace(/\/+$/, '')
    .replace(/^\/modules(?=\/|$)/, '')
    .replace(/^\/workflow(?=\/|$)/, '/process');
  return normalized || '/';
}

/** 基础档案、人员组织与产品建档会改变单据参照的当前身份和可选项。 */
export function isReferenceChange(event: RealtimeChangeEvent): boolean {
  if (event.scope === 'all') return true;
  if (!event.key) return false;
  const key = normalizeRealtimeKey(event.key);
  return ['/master-data', '/system', '/data-center'].some(
    (root) => key === root || key.startsWith(`${root}/`),
  );
}

/**
 * 把 SSE data 中的 JSON 归一化为事件；无法识别时返回 null。
 * 缺少 scope 时按携带的字段推断：有 key → list，有 billType/id → detail。
 */
export function normalizeRealtimeEvent(
  raw: unknown,
): RealtimeChangeEvent | null {
  if (!raw || typeof raw !== 'object') return null;
  const input = raw as Record<string, unknown>;

  let scope: RealtimeScope;
  if (
    input.scope === 'all' ||
    input.scope === 'list' ||
    input.scope === 'detail' ||
    input.scope === 'module'
  ) {
    scope = input.scope;
  } else if (input.scope != null) {
    return null;
  } else if (typeof input.key === 'string' && input.key) {
    scope = 'list';
  } else if (typeof input.billType === 'string' || input.id != null) {
    scope = 'detail';
  } else {
    return null;
  }

  const event: RealtimeChangeEvent = { scope };
  if (typeof input.key === 'string' && input.key) event.key = input.key;
  if (typeof input.billType === 'string' && input.billType)
    event.billType = input.billType;
  if (typeof input.id === 'string' || typeof input.id === 'number')
    event.id = input.id;
  if (typeof input.ts === 'number' && Number.isFinite(input.ts))
    event.ts = input.ts;
  if (scope === 'module' && !event.key) return null;
  return event;
}

/**
 * 事件是否命中某页面。
 *   - scope 'all' 命中所有页面；
 *   - list 要求 key 匹配（事件无 key 时不命中，避免误刷全部列表）；
 *   - detail 先比 key，再比 billType（两者都有 id 时再比 id）。
 */
export function matchesRealtimeEvent(
  event: RealtimeChangeEvent | null | undefined,
  target: RealtimeMatchTarget,
): boolean {
  if (!event) return false;
  if (event.scope === 'all') return true;
  if (event.scope === 'module') {
    if (!event.key || !target.key) return false;
    const moduleKey = normalizeRealtimeKey(event.key);
    const targetKey = normalizeRealtimeKey(target.key);
    return targetKey === moduleKey || targetKey.startsWith(`${moduleKey}/`);
  }
  if (event.scope !== target.scope) return false;
  if (event.id != null && target.id != null && String(event.id) !== String(target.id)) {
    return false;
  }

  if (
    event.key &&
    target.key &&
    normalizeRealtimeKey(event.key) === normalizeRealtimeKey(target.key)
  ) {
    return true;
  }

  if (target.scope === 'detail' && event.billType && target.billType) {
    if (event.billType !== target.billType) return false;
    const eventId = event.id != null ? String(event.id) : undefined;
    const targetId = target.id != null ? String(target.id) : undefined;
    return !eventId || !targetId || eventId === targetId;
  }

  return false;
}
